import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Mail, ShieldCheck, Copy, LifeBuoy } from "lucide-react";
import axios from "axios";
import Crown from "../components/Crown";
import PasswordInput from "../components/PasswordInput";
import { useAuth } from "../context/AuthContext";

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [step, setStep] = useState("request"); // request | confirm
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [devCode, setDevCode] = useState(null);
  const [expiresIn, setExpiresIn] = useState(null);

  const requestCode = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_ROOT}/auth/reset/request`, {
        email: email.trim(),
      });
      setExpiresIn(data.expires_in_minutes || 60);
      if (data.dev_code) {
        setDevCode(data.dev_code);
      }
      toast.success("If that email exists, a reset code has been issued.");
      setStep("confirm");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send reset code");
    } finally {
      setLoading(false);
    }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_ROOT}/auth/reset/confirm`, {
        email: email.trim(),
        code: code.trim(),
        new_password: newPassword,
      });
      // auto-sign-in on success
      if (setSession) {
        setSession(data.token, data.user);
      } else {
        localStorage.setItem("regalis_token", data.token);
        localStorage.setItem("regalis_user", JSON.stringify(data.user));
      }
      toast.success("Password reset. Welcome back.");
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-obsidian text-textmain flex items-center justify-center px-6 py-12">
      <div
        className="w-full max-w-md regal-card p-10"
        data-testid="forgot-password-card"
      >
        <div className="flex items-center gap-2 mb-6">
          <Crown className="w-8 h-8" strokeWidth={1.2} />
          <div>
            <span className="font-heading text-2xl block leading-none">Aurethion</span>
            <span className="wordmark-nexus text-[11px]">Crown</span>
          </div>
        </div>

        {step === "request" ? (
          <form onSubmit={requestCode} data-testid="reset-request-form">
            <p className="label-eyebrow">Password Recovery</p>
            <h2 className="font-heading text-3xl mt-2 mb-4">Forgot your password?</h2>
            <p className="text-sm text-textmuted mb-8 leading-relaxed">
              Type the email you used to sign up. We'll send a six-digit code
              you can use to set a new password.
            </p>

            <label className="block label-eyebrow mb-2" htmlFor="reset-email">Email</label>
            <input
              id="reset-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="reset-email-input"
              className="w-full mb-6 bg-obsidian border border-seam rounded-md px-4 py-3 text-textmain placeholder-[#52525B] focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 transition-colors"
              placeholder="you@domain.com"
              autoFocus
            />

            <button
              type="submit"
              disabled={loading || !email.trim()}
              data-testid="reset-request-btn"
              className="w-full bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-6 py-3 transition-all disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Mail className="w-4 h-4" strokeWidth={1.8} />
              )}
              Send reset code
            </button>

            <div className="mt-8 rounded-md border border-seam bg-elevated/40 p-4 text-xs text-textmuted leading-relaxed">
              <div className="flex items-center gap-2 mb-2 text-champagne">
                <LifeBuoy className="w-3.5 h-3.5" strokeWidth={1.8} />
                <span className="uppercase tracking-widest text-[10px]">Forgot the email too?</span>
              </div>
              Reach out to your Aurethion Crown administrator or{" "}
              <a
                href="mailto:support@righteoussolrsvp.com"
                data-testid="support-email-link"
                className="text-champagne hover:text-champagne-soft"
              >
                support@righteoussolrsvp.com
              </a>
              . An owner-level admin can verify your identity and issue a new code.
            </div>

            <p className="text-sm text-textmuted text-center mt-6">
              Remembered it?{" "}
              <Link to="/login" className="text-champagne hover:text-champagne-soft transition-colors" data-testid="back-to-login">
                Back to sign in
              </Link>
            </p>
          </form>
        ) : (
          <form onSubmit={confirmReset} data-testid="reset-confirm-form">
            <p className="label-eyebrow">Enter Code</p>
            <h2 className="font-heading text-3xl mt-2 mb-3">Set a new password</h2>
            <p className="text-sm text-textmuted mb-6 leading-relaxed">
              Enter the 6-digit code sent to <span className="text-champagne">{email}</span>
              {expiresIn ? ` (valid for ${expiresIn} minutes)` : ""}.
            </p>

            {devCode && (
              <div
                data-testid="dev-code-banner"
                className="mb-6 rounded-md border border-champagne/40 bg-champagne/5 p-3 text-xs"
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-champagne uppercase tracking-widest text-[10px] mb-1">
                      Preview mode — code shown here
                    </div>
                    <div className="font-mono text-lg text-textmain tracking-widest" data-testid="dev-code-value">
                      {devCode}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={async () => {
                      try {
                        if (navigator.clipboard && navigator.clipboard.writeText) {
                          await navigator.clipboard.writeText(devCode);
                          toast.success("Code copied");
                          return;
                        }
                        throw new Error("clipboard-unavailable");
                      } catch {
                        // Fallback for insecure contexts or denied permissions
                        try {
                          const ta = document.createElement("textarea");
                          ta.value = devCode;
                          ta.setAttribute("readonly", "");
                          ta.style.position = "fixed";
                          ta.style.opacity = "0";
                          document.body.appendChild(ta);
                          ta.select();
                          const ok = document.execCommand && document.execCommand("copy");
                          document.body.removeChild(ta);
                          if (ok) toast.success("Code copied");
                          else toast.error("Copy blocked — please copy the code by hand.");
                        } catch {
                          toast.error("Copy blocked — please copy the code by hand.");
                        }
                      }
                    }}
                    data-testid="dev-code-copy"
                    className="p-2 rounded border border-seam text-textmuted hover:text-champagne"
                    aria-label="Copy code"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </button>
                </div>
                <p className="text-[10px] text-textmuted mt-2 leading-relaxed">
                  When real email/SMS is wired, this banner disappears and the code arrives at your inbox / phone only.
                </p>
              </div>
            )}

            <label className="block label-eyebrow mb-2" htmlFor="reset-code">6-digit code</label>
            <input
              id="reset-code"
              type="text"
              inputMode="numeric"
              maxLength={6}
              required
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              data-testid="reset-code-input"
              className="w-full mb-5 bg-obsidian border border-seam rounded-md px-4 py-3 text-textmain text-center font-mono tracking-[0.5em] text-lg placeholder-[#52525B] focus:outline-none focus:border-champagne focus:ring-1 focus:ring-champagne/40 transition-colors"
              placeholder="••••••"
              autoFocus
            />

            <label className="block label-eyebrow mb-2" htmlFor="new-password">New password</label>
            <div className="mb-5">
              <PasswordInput
                id="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                testid="reset-new-password-input"
                autoComplete="new-password"
                placeholder="At least 6 characters"
              />
            </div>

            <label className="block label-eyebrow mb-2" htmlFor="confirm-password">Confirm new password</label>
            <div className="mb-8">
              <PasswordInput
                id="confirm-password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                testid="reset-confirm-password-input"
                autoComplete="new-password"
                placeholder="Retype it"
              />
            </div>

            <button
              type="submit"
              disabled={loading || code.length !== 6 || !newPassword}
              data-testid="reset-confirm-btn"
              className="w-full bg-champagne text-obsidian hover:bg-champagne-soft font-medium rounded-md px-6 py-3 transition-all disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4" strokeWidth={1.8} />
              )}
              Set new password
            </button>

            <p className="text-sm text-textmuted text-center mt-6">
              Didn't get a code?{" "}
              <button
                type="button"
                onClick={() => { setStep("request"); setCode(""); setDevCode(null); }}
                data-testid="reset-restart-btn"
                className="text-champagne hover:text-champagne-soft transition-colors"
              >
                Try a different email
              </button>
            </p>
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPassword;
